import { isNotDefined } from '@sgme/fp';
import { format } from 'date-fns';

import { logger } from '../components/SgwtBootstrap/logger';
import { getConfig } from '../config/config';
import { GrowlSlice } from '../store/growl/slice';
import type { AppDispatch } from '../store/store';
import { SECOND_IN_MILLIS } from '../utils/time';
import { dealStatusApi } from './bamSearch';
import { sendHelpRequestThunk } from './help-request';
import { optionApi } from './option';

const TIMEOUT_TOTAL_MILLIS = 10 * SECOND_IN_MILLIS;
const DEAL_STATUS_POLL_INTERVAL_MILLIS = 2 * SECOND_IN_MILLIS;

/**
 * Monitors execution for timeout (10s).
 * Starting at 2s, polls Deal status every 2s until 10s:
 *   - Deal status 'true'  → dispatch executionSuccess, stop polling
 *   - Deal status 'false' → dispatch executionRejected, stop polling
 *   - Deal request error  → retry after 2s
 * If still pending at 10s with no conclusive Deal status response:
 *   - updates growl to 'timeout', sends help request, dispatches executionRejected
 */
export function monitorTimeout(
  rfsId: string,
  dispatch: AppDispatch,
  {
    onExecSuccess,
    onExecFail,
    getPendingExecutionDetails,
  }: { onExecFail: () => void; onExecSuccess: () => void; getPendingExecutionDetails: () => unknown },
): () => void {
  const stopMonitor = () => {
    clearInterval(pollInterval);
    clearTimeout(tiemeoutedDealTimeout);
  };

  async function poll(attempt: number) {
    logger.logWarning(`Polling Deal status for option rfsId: ${rfsId} ${format(new Date(), 'HH:mm:ss')}`);

    const dealStatusResponse = await dispatch(
      dealStatusApi.endpoints.getDealStatus.initiate({ rfsId, attempt }),
    ).unwrap();
    dispatch(optionApi.internalActions.removeMutationResult({ fixedCacheKey: rfsId })); // TODO is this only for options ! good?

    // 404 indicates that the deal is not found. If it throws, we continue polling.
    // If the request is successful, we can stop polling.
    // It is either successful or failed.
    stopMonitor();
    if (dealStatusResponse) {
      onExecSuccess();
    } else {
      dispatch(
        GrowlSlice.actions.update({
          growlId: rfsId,
          data: { status: 'timeout' },
          options: { closeable: true },
        }),
      );
      onExecFail();
    }
  }

  function sendTimeoutedDeal(details: unknown) {
    stopMonitor();
    logger.logWarning(`Execution timeout for RFS ID: ${rfsId}`);

    dispatch(GrowlSlice.actions.update({ growlId: rfsId, data: { status: 'timeout' }, options: { closeable: true } }));
    onExecFail();

    dispatch(
      sendHelpRequestThunk({
        config: 'timeouteddeal',
        data: { multipassId: rfsId },
        details: JSON.stringify(details),
        templateId: getConfig().app.mailTemplateIds.templateIdForTimeout,
        templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForTimeoutConfirmation,
      }),
    );
  }

  let attempt = 0;
  const pollInterval = setInterval(async () => {
    const executionDetails = getPendingExecutionDetails();
    if (isNotDefined(executionDetails)) {
      return;
    }

    attempt += 1;

    try {
      await poll(attempt);
    } catch {
      // request failed, next interval tick will retry
    }
  }, DEAL_STATUS_POLL_INTERVAL_MILLIS);

  const tiemeoutedDealTimeout = setTimeout(() => {
    const executionDetails = getPendingExecutionDetails();
    if (isNotDefined(executionDetails)) {
      return;
    }

    sendTimeoutedDeal(executionDetails);
  }, TIMEOUT_TOTAL_MILLIS);

  return stopMonitor;
}
